import React, { useEffect, useState } from "react";
import "./ScheduleOpenHomeList.scss";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CommonEnums } from "../../../../enums/common.enums";
import { PropertyApi } from "../../../../utils/api/Property";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import FacebookIcon from "../../../../components/icons/FacebookIcon";
import InstagramIcon from "../../../../components/icons/InstagramIcon";
import LinkedInIcon from "../../../../components/icons/LinkedInIcon";
import GoogleBusinessIcon from "../../../../components/icons/GoogleBusinessIcon";
import YouTubeIcon from "../../../../components/icons/YouTubeIcon";
import TikTokIcon from "../../../../components/icons/TikTokIcon";
import PaginationButton from "../../buttons/PaginationButton";
import Loading from "../../../loading/Loading";
import CustomPageLoader from "../../../../components/loaders/CustomPageLoader";
import { CommonHelpers } from "../../../../utils/helpers/common.helpers";
import { clientRoutes } from "../../../../utils/routes";
import PropertyListMenuIcon from "../propertypost/list/PropertyListMenuIcon";
import PreviewModal from "../propertypost/view/PreviewModal";
import OpenHomeHeaderDateFilter from "./filter/OpenHomeHeaderDateFilter";
import OpenHomeListMenuIcon from "./filter/OpenHomeListMenuIcon";
import OpenHomeListFilter from "./filter/OpenHomeListFilter";
import { LocalStorageHelpers } from "../../../../utils/helpers/localstorage.helpers";
import {
  getAgentProfileImageUrl,
  getImageUrl,
} from "../../../../utils/helpers/common";
dayjs.extend(isBetween);

type WeekKey = "1st" | "2nd" | "3rd" | "4th" | "5th";

interface StateType {
  isLoading: boolean;
  totalPage: number;
  pageNumber: number;
  listData: Record<string, any[]>;
  search: string;
}

type FilterType = {
  year: number;
  month: number | null;
  week?: WeekKey;
  start_date?: string;
  end_date?: string;
  post_type?: string;
  post_status?: string;
  property_id?: string;
  status?: string;
  search?: string;
  agent_ref?: string;
  agency_ref?: string;
} | null;

interface DesignActivityStatus {
  image: boolean;
  story: boolean;
  video: boolean;
  reel: boolean;
}

const ImageRenderer = ({
  src,
  fallbackSrc,
  alt,
  className,
}: {
  src: string;
  fallbackSrc: string;
  alt: string;
  className?: string;
}) => {
  const [currentSrc, setCurrentSrc] = useState(fallbackSrc);

  useEffect(() => {
    const imageToLoad = new Image();
    imageToLoad.src = src;

    imageToLoad.onload = () => {
      setCurrentSrc(src);
    };

    imageToLoad.onerror = () => {
      setCurrentSrc(fallbackSrc);
    };
  }, [src, fallbackSrc]);

  return <img src={currentSrc} alt={alt} className={className} />;
};

const getCurrentWeekOfMonth = (): WeekKey => {
  const today = dayjs();
  const dayOfMonth = today.date();
  if (dayOfMonth <= 7) return "1st";
  if (dayOfMonth <= 14) return "2nd";
  if (dayOfMonth <= 21) return "3rd";
  if (dayOfMonth <= 28) return "4th";
  return "5th";
};

const ScheduleOpenHomeList = () => {
  const [state, setState] = useState<StateType>({
    isLoading: false,
    totalPage: 1,
    pageNumber: 1,
    listData: {},
    search: "",
  });

  const [filter, setFilter] = useState<FilterType>({
    year: dayjs().year(),
    month: dayjs().month(),
    week: getCurrentWeekOfMonth(),
  });
  const [searchParams] = useSearchParams();
  const subscriptionType = searchParams.get("subscription_type");
  const navigate = useNavigate();
  const [refreshingIds, setRefreshingIds] = useState<string[]>([]);
  const [counters, setCounters] = useState<Record<string, number>>({});
  const [activeTypes, setActiveTypes] = useState<Record<string, string>>({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("agent");
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  const [loadingPostType, setLoadingPostType] = useState<
    Record<string, string>
  >({});

  const [processingStatus, setProcessingStatus] = useState<
    Record<string, DesignActivityStatus>
  >({});

  const getOpenHomeList = async (page: number, search: string) => {
    const params: Record<string, any> = {};

    const agent_id = searchParams.get("agent_id") || "";
    const agency_id = searchParams.get("agency_id") || "";

    const property_id =
      searchParams.get(CommonEnums.queryParams.activitiesFilter.property_id) ||
      "";
    const start_date_param =
      searchParams.get(CommonEnums.queryParams.activitiesFilter.start_date) ||
      "";
    const end_date_param =
      searchParams.get(CommonEnums.queryParams.activitiesFilter.end_date) || "";
    const created_start_date_param =
      searchParams.get(
        CommonEnums.queryParams.activitiesFilter.created_start_date,
      ) || "";
    const created_end_date_param =
      searchParams.get(
        CommonEnums.queryParams.activitiesFilter.created_end_date,
      ) || "";
    const schedule_start_date_param =
      searchParams.get(
        CommonEnums.queryParams.activitiesFilter.schedule_start_date,
      ) || "";
    const schedule_end_date_param =
      searchParams.get(
        CommonEnums.queryParams.activitiesFilter.schedule_end_date,
      ) || "";
    const post_status =
      searchParams.get(CommonEnums.queryParams.activitiesFilter.post_status) ||
      "";
    const post_type =
      searchParams.get(CommonEnums.queryParams.activitiesFilter.post_type) ||
      "";

    if (property_id) params.property_id = property_id;
    if (agent_id) {
      const ids = agent_id.split(",").filter(Boolean);
      params.agent_ref = ids.length === 1 ? ids[0] : ids;
    }
    if (agency_id) {
      const ids = agency_id.split(",").filter(Boolean);
      params.agency_ref = ids.length === 1 ? ids[0] : ids;
    }
    if (post_status) params.post_status = post_status;
    if (post_type) params.post_type = post_type;

    if (filter?.property_id) params.property_id = filter.property_id;
    if (filter?.post_status) params.post_status = filter.post_status;
    if (filter?.post_type) params.post_type = filter.post_type;
    let isDateFilterActive = false;

    if (filter?.year && filter.month !== null && filter.week) {
      const startOfMonth = dayjs()
        .year(filter.year)
        .month(filter.month)
        .startOf("month");
      const endOfMonth = dayjs()
        .year(filter.year)
        .month(filter.month)
        .endOf("month");

      const ranges: Record<WeekKey, [number, number]> = {
        "1st": [1, 7],
        "2nd": [8, 14],
        "3rd": [15, 21],
        "4th": [22, 28],
        "5th": [29, endOfMonth.date()],
      };
      const [startDay, endDay] = ranges[filter.week];
      params.start_date = startOfMonth.date(startDay).format("YYYY-MM-DD");
      params.end_date = startOfMonth.date(endDay).format("YYYY-MM-DD");
      isDateFilterActive = true;
    }

    if (start_date_param) {
      params.start_date = start_date_param;
      isDateFilterActive = true;
    }
    if (end_date_param) {
      params.end_date = end_date_param;
      isDateFilterActive = true;
    }

    if (isDateFilterActive) {
      params.filter_date_by = "open_home_date";
    }

    if (start_date_param) params.start_date = start_date_param;
    if (end_date_param) params.end_date = end_date_param;

    if (schedule_start_date_param)
      params.schedule_start_date = schedule_start_date_param;
    if (schedule_end_date_param)
      params.schedule_end_date = schedule_end_date_param;

    if (created_start_date_param)
      params.created_start_date = created_start_date_param;
    if (created_end_date_param)
      params.created_end_date = created_end_date_param;

    setState((prev) => ({ ...prev, isLoading: true }));

    const res = await PropertyApi.getCombinedOpenHomeSchedules(params);

    if (res.data?.success) {
      let allData = res.data.data?.docs || res.data.data;

      const filteredByTab = allData.filter((item: any) => {
        const isAgencyPost =
          Array.isArray(item.agent_open_home_schedules) &&
          item.agent_open_home_schedules.length > 0;

        if (activeTab === "agency") {
          return isAgencyPost;
        } else {
          return !isAgencyPost;
        }
      });

      let finalData = filteredByTab.filter(
        (item: any) => item.post_status !== "trash",
      );

      finalData.sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

      const grouped = finalData.reduce((acc: any, item: any) => {
        const formattedDate = dayjs(item.open_home_date).format(
          "Do MMMM, YYYY",
        );
        if (!acc[formattedDate]) acc[formattedDate] = [];
        acc[formattedDate].push(item);
        return acc;
      }, {});

      const newCounters: Record<string, number> = {};
      allData.forEach((item: any) => {
        if (item.post_status === "processing") {
          if (!counters[item._id]) {
            refreshSingleItem(item._id);
          }
          newCounters[item._id] = 5;
        }
      });

      setState((prev) => ({
        ...prev,
        isLoading: false,
        listData: grouped,
        totalPage: res.data.data?.totalPages || 1,
      }));

      setCounters(newCounters);
    } else {
      toast.error(res?.error || "Failed to fetch open homes.");
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    getOpenHomeList(1, "");
  }, [activeTab]);

  const refreshSingleItem = async (id: string) => {
    try {
      setRefreshingIds((prev) => [...prev, id]);
      setCounters((prev) => ({ ...prev, [id]: 5 }));

      const res = await PropertyApi.getCombinedOpenHomeScheduleById(id);

      if (res.data?.success) {
        const updatedItem = res.data.data;
        setState((prev) => {
          const newListData: Record<string, any[]> = { ...prev.listData };
          for (const date in newListData) {
            newListData[date] = newListData[date].map((it: any) =>
              it._id === id ? updatedItem : it,
            );
          }
          return { ...prev, listData: newListData };
        });

        if (updatedItem.post_status !== "processing") {
          setCounters((prev) => {
            const newCounters = { ...prev };
            delete newCounters[id];
            return newCounters;
          });
        }
      }
    } catch (error) {
      console.error("Refresh failed", error);
    } finally {
      setRefreshingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const RefreshCountdown = ({
    id,
    counter,
    refreshInterval = 5,
    onRefresh,
  }: {
    id: string;
    counter: number;
    refreshInterval?: number;
    onRefresh: (id: string) => void;
  }) => {
    return (
      <div className="refresh-countdown">
        Refreshing record in {counter} seconds …{" "}
        <button
          type="button"
          className="refresh-now-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCounters((prev) => ({ ...prev, [id]: refreshInterval }));
            onRefresh(id);
          }}
        >
          Refresh Now
        </button>
      </div>
    );
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCounters((prev) => {
        const newCounters: Record<string, number> = { ...prev };
        const idsToRefresh: string[] = [];

        Object.keys(prev).forEach((id) => {
          const current = prev[id] ?? 5;
          if (current === 1) {
            idsToRefresh.push(id);
            newCounters[id] = 5;
          } else {
            newCounters[id] = current - 1;
          }
        });

        idsToRefresh.forEach((id) => {
          refreshSingleItem(id);
        });

        return newCounters;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const onClickPreview = () => {
    setState((prev) => ({
      ...prev,
      pageNumber: Math.max(prev.pageNumber - 1, 1),
    }));
  };

  const onClickNext = () => {
    setState((prev) => ({
      ...prev,
      pageNumber: Math.min(prev.pageNumber + 1, prev.totalPage),
    }));
  };

  const capitalizeFirstLetter = (str: string) => {
    if (!str) return "-";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  useEffect(() => {
    setState((prev) => ({ ...prev, pageNumber: 1 }));
  }, [filter]);

  useEffect(() => {
    getOpenHomeList(state.pageNumber, state.search);
  }, [state.search, state.pageNumber, searchParams, filter]);

  const formatPostType = (type?: string) => {
    if (!type) return "-";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleUpdatePostType = async (itemId: string, type: string) => {
    if (!itemId) {
      toast.error("Missing open home schedule ID");
      return;
    }

    try {
      setLoadingPostType((prev) => ({ ...prev, [itemId]: type }));

      const payload = {
        post_type: `${type.toLowerCase()}_post`,
        finalize: true,
      };

      const res = await PropertyApi.updateOpenHomePost(itemId, payload);

      if (res?.data?.success) {
        toast.success(
          `${
            type.charAt(0).toUpperCase() + type.slice(1)
          } post created successfully!`,
        );
        await getOpenHomeList(state.pageNumber, state.search);
      } else {
        toast.error(res?.data?.message || "Failed to create post type");
      }
    } catch (err) {
      console.error("Post type update failed:", err);
      toast.error("Something went wrong while updating post type");
    } finally {
      setLoadingPostType((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    }
  };

  const formatStatus = (status: string) => {
    if (!status) return "-";

    switch (status) {
      case "pending_for_social_media_upload":
        return "Pending Upload";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Cancelled";
      case "processing":
        return "Processing";
      case "errored":
        return "Error";
      default:
        return capitalizeFirstLetter(status.replace(/_/g, " "));
    }
  };

  const renderDesignImage = ({ item }: { item: any }) => {
    const designActivityRef = item.image_post_design_activity_ref?._id
      ? item.image_post_design_activity_ref
      : item.story_post_design_activity_ref?._id
        ? item.story_post_design_activity_ref
        : item.video_post_design_activity_ref?._id
          ? item.video_post_design_activity_ref
          : item.reel_post_design_activity_ref;

    const postType =
      designActivityRef?.design_post_type || item.design_post_type;

    if (!designActivityRef || !designActivityRef.design_task_id) {
      const baseType = (item.post_type || "image").replace("_post", "");

      return (
        <div className="activity-image-section image-post-mode">
          <div className="create-image-post-placeholder">
            <p className="not-available-for-post-type">
              {baseType.charAt(0).toUpperCase() + baseType.slice(1)} post is not
              available for the open home.
            </p>
            <button
              className="confirm-btn"
              disabled={loadingPostType[item._id] === baseType}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleUpdatePostType(item._id, baseType);
              }}
            >
              {loadingPostType[item._id] === baseType
                ? `Creating ${baseType}...`
                : `Create ${
                    baseType.charAt(0).toUpperCase() + baseType.slice(1)
                  } Post`}
            </button>
          </div>
        </div>
      );
    }

    const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
    const designTaskStatus = designActivityRef?.design_task_id?.task_status;
    const designStatus = designActivityRef?.design_status;

    const assetUrl = assetUrlRaw
      ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
      : null;

    const isErrored = designTaskStatus === CommonEnums.taskStatus.errored;
    const isProcessing = !(
      designStatus === "pending_for_social_media_upload" &&
      designTaskStatus === CommonEnums.taskStatus.completed &&
      assetUrl
    );

    // Error fallback image
    if (isErrored) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error"
            className="activity-thumbnail placeholder-image"
          />
        </div>
      );
    }

    // Processing loader
    if (isProcessing) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    // Post type specific rendering
    if (postType === CommonEnums.postType.story_post.key) {
      return (
        <div className="activity-image-section story-post-image-container">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Property"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.video_post.key) {
      return (
        <div className="activity-image-section video-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Property"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.reel_post.key) {
      return (
        <div className="activity-image-section reel-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Property"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    // Default image rendering
    return (
      <div className="activity-image-section">
        <img
          src={
            assetUrl ||
            "/images/image-video-generation-error-fallback-image-for-card.png"
          }
          alt="Property"
          className="activity-thumbnail"
          onError={(e: any) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              "/images/image-video-generation-error-fallback-image-for-card.png";
          }}
        />
      </div>
    );
  };

  const renderDesignImageByType = ({
    item,
    selectedType,
  }: {
    item: any;
    selectedType?: string;
  }) => {
    const loggedInUser = LocalStorageHelpers.getUserData();
    const currentAgentId = loggedInUser?._id || "";

    const coAgent = item?.agent_ref;

    const CoAgentInfo =
      coAgent && coAgent?._id !== currentAgentId ? (
        <div className="agent-info">
          <img
            // src={
            //   getImageUrl(
            //     coAgent?.profile_image_ref?.compressed_file_path || "",
            //   ) || "/images/profile-placeholder-image.png"
            // }
            src={
              getAgentProfileImageUrl(coAgent?.profile_image_ref) ||
              getImageUrl(
                coAgent?.profile_image_ref?.compressed_file_path || "",
              ) ||
              "/images/profile-placeholder-image.png"
            }
            alt="Co-Agent"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/profile-placeholder-image.png";
            }}
          />
          <div>
            <p>
              {item.agent_ref?.principal_agent_first_name}{" "}
              {item.agent_ref?.principal_agent_last_name}
            </p>
            <p>
              {item.agent_ref?.principal_phone_country_code}{" "}
              {item.agent_ref?.principal_agent_mobile_number}
            </p>
          </div>
        </div>
      ) : null;
    const type =
      selectedType || item.post_type?.replace("_post", "") || "image";

    const designActivityRef =
      type === "image"
        ? item.image_post_design_activity_ref
        : type === "story"
          ? item.story_post_design_activity_ref
          : type === "video"
            ? item.video_post_design_activity_ref
            : type === "reel"
              ? item.reel_post_design_activity_ref
              : null;

    if (
      !designActivityRef ||
      !designActivityRef._id ||
      !designActivityRef.design_task_id?._id
    ) {
      return (
        <div className="activity-image-section image-post-mode">
          <div className="create-image-post-placeholder">
            <p className="not-available-for-post-type">
              {type.charAt(0).toUpperCase() + type.slice(1)} post is not
              available for this open home.
            </p>
            <p className="post-type-label">
              Create {type.charAt(0).toUpperCase() + type.slice(1)} Post
            </p>
          </div>
        </div>
      );
    }

    if (item.post_status === "cancelled") {
      const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
      const fallbackUrl = "/images/square-fallback-image-for-image-preview.png";
      const assetUrl = assetUrlRaw
        ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
        : "/images/image-video-generation-error-fallback-image-for-card.png";

      const postTypeClass =
        type === "story"
          ? "story-post-image-container"
          : type === "video"
            ? "video-placeholder-collection"
            : type === "reel"
              ? "reel-placeholder-collection"
              : "";

      return (
        <div
          className={`activity-image-section image-placeholder-container ${postTypeClass}`}
        >
          <ImageRenderer
            src={assetUrl}
            fallbackSrc={fallbackUrl}
            alt="Open Home"
            className="activity-thumbnail"
          />
          {item.schedule_type === "agency" ||
          (item.agent_open_home_schedules?.length > 0 &&
            !item.schedule_type) ? (
            <div className="agent-post-badge">Agency Post</div>
          ) : (
            <div className="agent-post-badge">Agent Post</div>
          )}

          {CoAgentInfo}
        </div>
      );
    }

    if (item.post_status === "errored") {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error"
            className="activity-thumbnail placeholder-image"
          />
          {item.schedule_type === "agency" ||
          (item.agent_open_home_schedules?.length > 0 &&
            !item.schedule_type) ? (
            <div className="agent-post-badge">Agency Post</div>
          ) : (
            <div className="agent-post-badge">Agent Post</div>
          )}

          {CoAgentInfo}
        </div>
      );
    }

    if (item.post_status === "processing") {
      return (
        <div className="activity-image-section image-placeholder-container">
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    if (item.post_status === "pending_for_social_media_upload") {
      const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
      const fallbackUrl = "/images/square-fallback-image-for-image-preview.png";
      const assetUrl = assetUrlRaw
        ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
        : "/images/image-video-generation-error-fallback-image-for-card.png";

      const postTypeClass =
        type === "story"
          ? "story-post-image-container"
          : type === "video"
            ? "video-placeholder-collection"
            : type === "reel"
              ? "reel-placeholder-collection"
              : "";

      return (
        <div className={`activity-image-section ${postTypeClass}`}>
          <ImageRenderer
            src={assetUrl}
            fallbackSrc={fallbackUrl}
            alt="Open Home"
            className="activity-thumbnail"
          />
          {item.schedule_type === "agency" ||
          (item.agent_open_home_schedules?.length > 0 &&
            !item.schedule_type) ? (
            <div className="agent-post-badge">Agency Post</div>
          ) : (
            <div className="agent-post-badge">Agent Post</div>
          )}

          {CoAgentInfo}
        </div>
      );
    }

    return (
      <div className="activity-image-section image-placeholder-container">
        <img
          src="/images/image-video-generation-error-fallback-image-for-card.png"
          alt="Property"
          className="activity-thumbnail placeholder-image"
        />
        {item.schedule_type === "agency" ||
        (item.agent_open_home_schedules?.length > 0 && !item.schedule_type) ? (
          <div className="agent-post-badge">Agency Post</div>
        ) : (
          <div className="agent-post-badge">Agent Post</div>
        )}

        {CoAgentInfo}
      </div>
    );
  };

  const formatListingType = (type: string) => {
    if (!type) return "-";
    switch (type) {
      case "rent":
        return "For Rent";
      case "sale":
        return "For Sale";
      default:
        return type;
    }
  };

  const getPreviewImages = (item: any, activeTypes: Record<string, string>) => {
    const currentType =
      activeTypes[item._id] || item.post_type?.replace("_post", "") || "image";

    const designActivityRef =
      currentType === "image"
        ? item.image_post_design_activity_ref
        : currentType === "story"
          ? item.story_post_design_activity_ref
          : currentType === "video"
            ? item.video_post_design_activity_ref
            : currentType === "reel"
              ? item.reel_post_design_activity_ref
              : null;

    if (!designActivityRef?.design_task_id) {
      return [
        "/images/image-video-generation-error-fallback-image-for-card.png",
      ];
    }

    const parentTask = designActivityRef.design_task_id;

    let paths: string[] = [
      parentTask.output_story_path,
      parentTask.output_image_path,
      ...(parentTask.output_gallery_paths || []),
    ].filter(Boolean);

    if (Array.isArray(parentTask.child_design_tasks)) {
      parentTask.child_design_tasks.forEach((child: any, index: number) => {
        if (!child) return;

        const childPaths: string[] = [
          ...(child.output_gallery_paths || []),
          ...(child.output_image_path ? [child.output_image_path] : []),
          ...(child.output_story_path ? [child.output_story_path] : []),
        ];

        paths.push(...childPaths);
      });
    }

    const result = paths.length
      ? paths.map((p: string) =>
          CommonHelpers.getImageUrlFromImageTool({ path: p }),
        )
      : ["/images/image-video-generation-error-fallback-image-for-card.png"];

    return result;
  };

  const handleSelectPostType = async (item: any, type: string) => {
    setActiveTypes((prev) => ({
      ...prev,
      [item._id]: type,
    }));

    try {
      const res = await PropertyApi.getCombinedOpenHomeScheduleById(item._id);

      if (res.data?.success) {
        const updatedItem = res.data.data;
        setState((prev) => {
          const newListData: Record<string, any[]> = { ...prev.listData };
          for (const date in newListData) {
            newListData[date] = newListData[date].map((it: any) =>
              it._id === item._id ? updatedItem : it,
            );
          }
          return { ...prev, listData: newListData };
        });
      }
    } catch (err) {
      console.error("Failed to refresh post type", err);
    }
  };

  const getOpenHomeLink = (item: any) => {
    const hasAgentOpenHome =
      Array.isArray(item.agent_open_home_schedules) &&
      item.agent_open_home_schedules.length > 0;

    const basePath = hasAgentOpenHome
      ? "/workspace/agency-schedule-open-home/view"
      : "/workspace/agent-open-home/view";

    return `${basePath}/${item._id}`;
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="auction-activities-list-container">
          <div className="manage-invites-list-heading">
            <div
              className="propertie-view-back-btn"
              onClick={() => navigate(clientRoutes.workspace)}
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to Workspace</p>
            </div>
            <p className="heading-text">Open Home Schedules</p>
          </div>

          <div className="toggle-container">
            <div className="openhome-post-toggle">
              <button
                className={`toggle-btn ${
                  activeTab === "agent" ? "active" : ""
                }`}
                onClick={() => setActiveTab("agent")}
              >
                Agent Posts
              </button>
              <button
                className={`toggle-btn ${
                  activeTab === "agency" ? "active" : ""
                }`}
                onClick={() => setActiveTab("agency")}
              >
                Agency Posts
              </button>
            </div>
          </div>

          <OpenHomeHeaderDateFilter
            value={filter}
            onChange={(val) => setFilter(val)}
            onClickRefresh={() =>
              getOpenHomeList(state.pageNumber, state.search)
            }
          />

          {state?.isLoading ? (
            <div className="list-page-loading">
              <Loading />
            </div>
          ) : Object.entries(state.listData).length === 0 ? (
            <div className="data-not-found">
              <p>List is empty!</p>
            </div>
          ) : (
            <div className="responsive-grid-container">
              {Object.entries(state.listData).map(([date, auctions]) => (
                <React.Fragment key={date}>
                  <div className="filter-date">{date}</div>
                  {auctions.map((item: any) => (
                    <div key={item._id} className="activities-card-wrapper">
                      <div className="activity-card">
                        {/* <Link
                        to={`/workspace/schedule-open-home/view/${item._id}?subscription_type=${subscriptionType}`}
                      > */}
                        {/* <div className="activity-image-section">
                            <img
                              src="/images/square-fallback-image-for-image-preview.png"
                              data-src={
                                item.property_id?.thumbnail_image_ref
                                  ?.image_url ||
                                "/images/image-video-generation-error-fallback-image-for-card.png"
                              }
                              alt="Property"
                              className="activity-thumbnail"
                              onLoad={(e: any) => {
                                const target = e.target as HTMLImageElement;
                                const realSrc = target.getAttribute("data-src");
                                if (realSrc && target.src !== realSrc) {
                                  target.src = realSrc;
                                }
                              }}
                              onError={(e: any) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "/images/image-video-generation-error-fallback-image-for-card.png";
                              }}
                            />
                          </div> */}

                        {/* {renderDesignImage({ item })} */}

                        {/* {renderDesignImageByType({
                          item,
                          selectedType:
                            activeTypes[item._id] ||
                            item.post_type?.replace("_post", "") ||
                            "image",
                        })} */}
                        {(() => {
                          const AgentInfo = (
                            <div className="agent-info">
                              <img
                                // src={
                                //   getImageUrl(
                                //     item.agent_ref?.profile_image_ref
                                //       ?.compressed_file_path || ""
                                //   ) || "/images/profile-placeholder-image.png"
                                // }
                                src={
                                  getAgentProfileImageUrl(
                                    item.agent_ref?.profile_image_ref,
                                  ) ||
                                  getImageUrl(
                                    item.agent_ref?.profile_image_ref
                                      ?.compressed_file_path || "",
                                  ) ||
                                  "/images/profile-placeholder-image.png"
                                }
                                alt="Co-Agent"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    "/images/profile-placeholder-image.png";
                                }}
                              />
                              <div>
                                <p>
                                  {item.agent_ref?.principal_agent_first_name}{" "}
                                  {item.agent_ref?.principal_agent_last_name}
                                </p>
                                <p>
                                  {item.agent_ref?.principal_phone_country_code}{" "}
                                  {
                                    item.agent_ref
                                      ?.principal_agent_mobile_number
                                  }
                                </p>
                              </div>
                            </div>
                          );

                          const selectedType =
                            activeTypes[item._id] ||
                            item.post_type?.replace("_post", "") ||
                            "image";

                          const designActivityRef =
                            selectedType === "image"
                              ? item.image_post_design_activity_ref
                              : selectedType === "story"
                                ? item.story_post_design_activity_ref
                                : selectedType === "video"
                                  ? item.video_post_design_activity_ref
                                  : selectedType === "reel"
                                    ? item.reel_post_design_activity_ref
                                    : null;

                          if (
                            !designActivityRef ||
                            !designActivityRef._id ||
                            !designActivityRef.design_task_id?._id
                          ) {
                            return (
                              <div className="activity-image-section image-post-mode">
                                <div className="create-image-post-placeholder">
                                  <p className="not-available-for-post-type">
                                    Design is not available!
                                  </p>
                                </div>
                                {item.schedule_type === "agency" ||
                                (item.agent_open_home_schedules?.length > 0 &&
                                  !item.schedule_type) ? (
                                  <div className="agent-post-badge">
                                    Agency Post
                                  </div>
                                ) : (
                                  <div className="agent-post-badge">
                                    Agent Post
                                  </div>
                                )}
                                {!item.agent_open_home_schedules && AgentInfo}
                              </div>
                            );
                          }

                          const hasAgentOpenHome =
                            Array.isArray(item.agent_open_home_schedules) &&
                            item.agent_open_home_schedules.length > 0;

                          const linkTo = hasAgentOpenHome
                            ? `/workspace/agency-schedule-open-home/view/${item._id}`
                            : `/workspace/agent-open-home/view/${item._id}`;

                          return (
                            <Link to={linkTo}>
                              {renderDesignImageByType({ item, selectedType })}
                            </Link>
                          );
                        })()}

                        {/* this is using for create post  */}
                        {/* <div className="activity-image-section image-post-mode">
                          <div className="create-image-post-placeholder">
                            <p className="not-available-for-post-type">
                              Image post is not available for the open home.
                            </p>
                            <p className="post-type-label">Create Image Post</p>
                          </div>
                        </div> */}

                        {/* this is using for proccessing design  */}
                        {/* <div className="activity-image-section video-processing-mode">
                          <div className="video-processing-placeholder">
                            <div className="spinner"></div>
                            <p className="processing-text">Processing…</p>
                          </div>
                        </div> */}
                        {/* </Link> */}

                        <div className="activity-content-section">
                          <div className="activity-options-menu">
                            <OpenHomeListMenuIcon
                              previewImageUrl={
                                getPreviewImages(item, activeTypes)[0]
                              }
                              onViewDetails={() =>
                                navigate(`${getOpenHomeLink(item)}`)
                              }
                              onPreview={() => {
                                setPreviewItem(item);
                                setIsPreviewOpen(true);
                              }}
                              onUpdated={() =>
                                getOpenHomeList(state.pageNumber, state.search)
                              }
                            />
                          </div>

                          <div className="home-activity-details">
                            <div className="activity-post-type-icons">
                              {[
                                {
                                  type: "image",
                                  icon: "/images/workspace/image.svg",
                                  alt: "Image Post",
                                },
                                {
                                  type: "story",
                                  icon: "/images/workspace/image.svg",
                                  alt: "Story Post",
                                },
                                {
                                  type: "video",
                                  icon: "/images/workspace/video.svg",
                                  alt: "Video Post",
                                },
                                {
                                  type: "reel",
                                  icon: "/images/workspace/video.svg",
                                  alt: "Reel Post",
                                },
                              ].map(({ type, icon, alt }) => {
                                const currentType =
                                  activeTypes[item._id] ||
                                  item.post_type?.replace("_post", "") ||
                                  "image";

                                return (
                                  <div
                                    key={type}
                                    className={`post-type-icon ${type}-icon ${
                                      currentType === type ? "active" : ""
                                    }`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSelectPostType(item, type);
                                    }}
                                  >
                                    <img src={icon} alt={alt} />
                                  </div>
                                );
                              })}
                            </div>

                            {(() => {
                              const currentType =
                                activeTypes[item._id] ||
                                item.post_type?.replace("_post", "") ||
                                "image";

                              const designActivityRef =
                                currentType === "image"
                                  ? item.image_post_design_activity_ref
                                  : currentType === "story"
                                    ? item.story_post_design_activity_ref
                                    : currentType === "video"
                                      ? item.video_post_design_activity_ref
                                      : currentType === "reel"
                                        ? item.reel_post_design_activity_ref
                                        : null;

                              if (
                                designActivityRef &&
                                designActivityRef._id &&
                                designActivityRef.design_task_id?._id
                              ) {
                                const assetUrlRaw =
                                  designActivityRef?.design_task_id
                                    ?.output_image_path;
                                const designTaskStatus =
                                  designActivityRef?.design_task_id
                                    ?.task_status;
                                const designStatus =
                                  designActivityRef?.design_status;

                                const assetUrl = assetUrlRaw
                                  ? CommonHelpers.getImageUrlFromImageTool({
                                      path: assetUrlRaw,
                                    })
                                  : null;

                                const hasCounter =
                                  counters[item._id] !== undefined;

                                return (
                                  item.post_status === "processing" &&
                                  hasCounter && (
                                    <RefreshCountdown
                                      id={item._id}
                                      counter={counters[item._id] ?? 5}
                                      refreshInterval={5}
                                      onRefresh={refreshSingleItem}
                                    />
                                  )
                                );
                              }

                              return null;
                            })()}

                            <Link to={getOpenHomeLink(item)}>
                              <div className="detail-row">
                                <div>
                                  <div className="label">Agency Name</div>
                                  <div className="value">
                                    {item.agency_ref?.agency_trading_name ||
                                      "-"}
                                  </div>
                                </div>
                                <div>
                                  <div className="label">Post</div>
                                  <div className="value">
                                    {" "}
                                    {formatPostType(
                                      activeTypes[item._id]
                                        ? `${activeTypes[item._id]}_post`
                                        : item.post_type,
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div className="label">Open Home Date</div>
                                  <div className="value">
                                    {item.open_home_date
                                      ? dayjs(item.open_home_date).isValid()
                                        ? dayjs(item.open_home_date).format(
                                            "MMM DD, YYYY",
                                          )
                                        : "-"
                                      : "-"}
                                  </div>
                                </div>
                              </div>
                            </Link>
                            <Link to={getOpenHomeLink(item)}>
                              <div className="detail-row">
                                <div>
                                  <div className="label">Scheduled On</div>
                                  <div className="value">
                                    {item.schedule_datetime
                                      ? dayjs(item.schedule_datetime).format(
                                          "MMM DD, YYYY hh:mm A",
                                        )
                                      : "-"}
                                  </div>
                                </div>
                                <div>
                                  <div className="label">Current Status</div>
                                  <div className="value">
                                    {(() => {
                                      const currentType =
                                        activeTypes[item._id] ||
                                        item.post_type?.replace("_post", "") ||
                                        "image";

                                      const designActivityRef =
                                        currentType === "image"
                                          ? item.image_post_design_activity_ref
                                          : currentType === "story"
                                            ? item.story_post_design_activity_ref
                                            : currentType === "video"
                                              ? item.video_post_design_activity_ref
                                              : currentType === "reel"
                                                ? item.reel_post_design_activity_ref
                                                : null;

                                      const status =
                                        item.post_status === "cancelled"
                                          ? "cancelled"
                                          : designActivityRef?.design_status ||
                                            item.post_status ||
                                            "-";

                                      return `${formatStatus(status)}`;
                                    })()}
                                  </div>
                                </div>
                                <div>
                                  <div className="label">Listing Type</div>
                                  <div className="value">
                                    {formatListingType(item.listing_type)}
                                  </div>
                                </div>
                                <div className="detail-row">
                                  <div>
                                    <div className="label">Template</div>
                                    <div className="value">
                                      {(() => {
                                        const currentType =
                                          activeTypes[item._id] ||
                                          item.post_type?.replace(
                                            "_post",
                                            "",
                                          ) ||
                                          "image";

                                        const designActivityRef =
                                          currentType === "image"
                                            ? item.image_post_design_activity_ref
                                            : currentType === "story"
                                              ? item.story_post_design_activity_ref
                                              : currentType === "video"
                                                ? item.video_post_design_activity_ref
                                                : currentType === "reel"
                                                  ? item.reel_post_design_activity_ref
                                                  : null;

                                        return (
                                          designActivityRef?.design_template_id
                                            ?.name || "-"
                                        );
                                      })()}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="label">Created on</div>
                                    <div className="value">
                                      {dayjs(item.createdAt).format(
                                        "MMM DD, YYYY hh:mm A",
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="label">Last Updated on</div>
                                    <div className="value">
                                      {dayjs(item.updatedAt).format(
                                        "MMM DD, YYYY hh:mm A",
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </div>

                          {/* <div className="activity-social-icons">
                            <FacebookIcon />
                            <InstagramIcon />
                            <LinkedInIcon />
                            <GoogleBusinessIcon />
                            <YouTubeIcon />
                            <TikTokIcon />
                          </div> */}
                        </div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}

          <OpenHomeListFilter />

          <PreviewModal
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            images={
              previewItem ? getPreviewImages(previewItem, activeTypes) : []
            }
          >
            {previewItem &&
              renderDesignImageByType({
                item: previewItem,
                selectedType:
                  activeTypes[previewItem._id] ||
                  previewItem.post_type?.replace("_post", "") ||
                  "image",
              })}
          </PreviewModal>

          <PaginationButton
            total_page={state.totalPage}
            page_number={state.pageNumber}
            onClickNext={onClickNext}
            onClickPreview={onClickPreview}
            is_loading={state.isLoading}
          />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default ScheduleOpenHomeList;
